import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { getAllConditionTaskIds } from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import {
  EMPTY_ELK_EDGE_PATHS,
  ELK_FOR_LAYOUT_VERSION,
  LAYOUT_HOLD_SAFETY_MS,
} from './flowchartLayout/constants';
import { computeMultiCampaignLayout } from './flowchartLayout/computeMultiCampaignLayout';
import { useLayoutEdgeFadeChoreography } from './flowchartLayout/useLayoutEdgeFadeChoreography';
import { usePostMeasureElkRelayout } from './flowchartLayout/usePostMeasureElkRelayout';
import { useElkRelayoutOnNodeDimensionsChange } from './flowchartLayout/useElkRelayoutOnNodeDimensionsChange';
import { useElkAsyncLayoutEffect } from './flowchartLayout/useElkAsyncLayoutEffect';

/**
 * useFlowchartLayout — layout version, ELK async path, multi-campaign memo, tidy animation.
 * Helpers live under hooks/flowchartLayout/.
 */
export function useFlowchartLayout({
  tasks,
  nodeDimensions,
  autoArrange: _autoArrange,
  isMultiCampaignMode = false,
  chainOrder = null,
  onChainOrderChange: _onChainOrderChange = null,
  campaignId = null,
  customPositionsRef = null,
  clearCustomPositionsBeforeElkApplyRef = null,
  connectLayoutCenterRef = null,
  pendingCenterTaskIdRef = null,
  hasActiveFilters = false,
}) {
  const [layoutVersion, setLayoutVersion] = useState(0);
  const layoutVersionRef = useRef(layoutVersion);
  layoutVersionRef.current = layoutVersion;
  const layoutRequestModeRef = useRef(null);
  const layoutHoldRef = useRef(false);
  const layoutHoldTimeoutRef = useRef(null);

  const endLayoutHold = useCallback(() => {
    if (layoutHoldTimeoutRef.current != null) {
      clearTimeout(layoutHoldTimeoutRef.current);
      layoutHoldTimeoutRef.current = null;
    }
    layoutHoldRef.current = false;
  }, []);

  const beginLayoutHold = useCallback(() => {
    layoutHoldRef.current = true;
    if (layoutHoldTimeoutRef.current != null) {
      clearTimeout(layoutHoldTimeoutRef.current);
    }
    layoutHoldTimeoutRef.current = setTimeout(() => {
      layoutHoldTimeoutRef.current = null;
      layoutHoldRef.current = false;
    }, LAYOUT_HOLD_SAFETY_MS);
  }, []);

  const [isAnimatingLayout, setIsAnimatingLayout] = useState(false);

  /** Toolbar spinner for animated full Tidy only — avoids coupling to `isAnimatingLayout` (filter interrupts, multi-campaign fades, etc.). */
  const [isAnimatedTidyToolbarSession, setIsAnimatedTidyToolbarSession] = useState(false);
  const animatedTidyToolbarArmedRef = useRef(false);

  const resetAnimatedTidyToolbarSession = useCallback(() => {
    animatedTidyToolbarArmedRef.current = false;
    setIsAnimatedTidyToolbarSession(false);
  }, []);

  const completeAnimatedTidyToolbarSession = useCallback(() => {
    if (!animatedTidyToolbarArmedRef.current) return;
    animatedTidyToolbarArmedRef.current = false;
    setIsAnimatedTidyToolbarSession(false);
  }, []);

  const {
    layoutAnimationTimeoutRef,
    edgeFadeTimeoutRef,
    startLayoutEdgeHiddenPhase,
    scheduleLayoutEdgeRevealAfter,
    triggerEdgeFadeIn,
  } = useLayoutEdgeFadeChoreography(setIsAnimatingLayout, {
    onLayoutRevealPhaseComplete: completeAnimatedTidyToolbarSession,
  });

  const taskIdSignature = useMemo(() => tasks.map((t) => t.id).sort().join('|'), [tasks]);

  const dependencySignature = useMemo(() => {
    const getEffectiveDeps = (task) => {
      const ids = getAllConditionTaskIds(task.unlockConditions, tasks);
      if (ids.length > 0) return [...ids].sort().join(',');
      const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
      return deps
        .map((d) => {
          if (typeof d === 'string') return d;
          return d?.taskId || d?.nodeId || '';
        })
        .filter(Boolean)
        .sort()
        .join(',');
    };
    return tasks.map((t) => `${t.id}:${getEffectiveDeps(t)}`).sort().join('|');
  }, [tasks]);

  const nodeDimensionsRef = useRef(nodeDimensions);
  nodeDimensionsRef.current = nodeDimensions;

  const tasksForLayoutRef = useRef(tasks);
  const lastTaskIdSignatureRef = useRef(taskIdSignature);
  const lastDependencySignatureRef = useRef(dependencySignature);
  const lastLayoutVersionRef = useRef(layoutVersion);
  const lastIsMultiCampaignModeRef = useRef(isMultiCampaignMode);
  const lastChainOrderRef = useRef(chainOrder ? chainOrder.join(',') : null);
  const postMeasureElkRelayoutDoneRef = useRef(false);

  const [lockedTaskIdSignature, setLockedTaskIdSignature] = useState(taskIdSignature);
  const [lockedDependencySignature, setLockedDependencySignature] = useState(dependencySignature);

  const taskIdsChanged = taskIdSignature !== lastTaskIdSignatureRef.current;
  const layoutVersionChanged = layoutVersion !== lastLayoutVersionRef.current;
  const dependencyChanged = dependencySignature !== lastDependencySignatureRef.current;
  const modeChanged = isMultiCampaignMode !== lastIsMultiCampaignModeRef.current;
  const chainOrderSignature = chainOrder ? chainOrder.join(',') : null;

  const allTasksHavePositions = tasks.every((task) => task.x !== undefined && task.y !== undefined);

  // Distinguish a *filter* change (subset of already-known tasks) from a *structural* change
  // such as pasting a workflow (brand-new task ids). A filter only ever re-shows tasks we have
  // seen before; pasted tasks are ids we have never seen. Treating a paste as a filter change
  // skips the ELK pass, so freshly pasted edges stay on the legacy getEdgePath routing until a
  // manual Tidy. Tracking ids ever seen this session keeps un-filtering (re-showing previously
  // hidden tasks) classified as a filter change, not a paste.
  const everSeenTaskIdsRef = useRef(null);
  const pasteAutoLayoutPrevIdsRef = useRef(null);
  if (everSeenTaskIdsRef.current === null) {
    everSeenTaskIdsRef.current = new Set(tasks.map((t) => t.id));
  }
  const hasBrandNewTaskIds = tasks.some((t) => !everSeenTaskIdsRef.current.has(t.id));

  const prevIdCount = lastTaskIdSignatureRef.current
    ? lastTaskIdSignatureRef.current.split('|').filter(Boolean).length
    : 0;
  const nextIdCount = taskIdSignature.split('|').filter(Boolean).length;
  const isStructuralDeletion =
    taskIdsChanged &&
    !hasActiveFilters &&
    !hasBrandNewTaskIds &&
    nextIdCount < prevIdCount;

  const isLikelyFilterChange =
    taskIdsChanged &&
    allTasksHavePositions &&
    !layoutVersionChanged &&
    !hasBrandNewTaskIds &&
    !isStructuralDeletion;

  if (isStructuralDeletion) {
    // Same render as the implicit ELK effect — hold before that pass can snap.
    // `triggerLayout` in the delete effect refreshes the safety timeout.
    layoutHoldRef.current = true;
    postMeasureElkRelayoutDoneRef.current = true;
  }

  for (const t of tasks) everSeenTaskIdsRef.current.add(t.id);

  const shouldUpdateLayout = isMultiCampaignMode
    ? layoutVersionChanged || modeChanged || (taskIdsChanged && !isLikelyFilterChange)
    : (taskIdsChanged && !isLikelyFilterChange) ||
      layoutVersionChanged ||
      dependencyChanged;

  if (shouldUpdateLayout) {
    const isFilterChange = taskIdsChanged && !layoutVersionChanged && !dependencyChanged;

    if (isFilterChange && isAnimatingLayout) {
      if (layoutAnimationTimeoutRef.current) {
        clearTimeout(layoutAnimationTimeoutRef.current);
        layoutAnimationTimeoutRef.current = null;
      }
      if (edgeFadeTimeoutRef.current) {
        clearTimeout(edgeFadeTimeoutRef.current);
        edgeFadeTimeoutRef.current = null;
      }
      setIsAnimatingLayout(false);
      resetAnimatedTidyToolbarSession();
    }

    if (
      isMultiCampaignMode &&
      (dependencyChanged || layoutVersionChanged)
    ) {
      triggerEdgeFadeIn();
    }
    tasksForLayoutRef.current = tasks;
    lastTaskIdSignatureRef.current = taskIdSignature;
    lastDependencySignatureRef.current = dependencySignature;
    lastLayoutVersionRef.current = layoutVersion;
    lastIsMultiCampaignModeRef.current = isMultiCampaignMode;
    lastChainOrderRef.current = chainOrderSignature;

    setLockedTaskIdSignature(taskIdSignature);
    setLockedDependencySignature(dependencySignature);
  }

  tasksForLayoutRef.current = tasks;

  const [elkLayoutResult, setElkLayoutResult] = useState(null);

  const lastCommittedElkPositionsRef = useRef({});
  const lastCommittedElkChainInfoRef = useRef([]);
  const lastCommittedElkEdgePathsRef = useRef({});
  /** Pin key of the last layout computed from real card sizes. Pre-measure passes must not set this. */
  const lastCommittedLayoutPinKeyRef = useRef(null);

  const initialElkSettlementRef = useRef(true);

  useEffect(() => {
    everSeenTaskIdsRef.current = null;
    pasteAutoLayoutPrevIdsRef.current = null;
    initialElkSettlementRef.current = true;
    postMeasureElkRelayoutDoneRef.current = false;
    lastCommittedElkPositionsRef.current = {};
    lastCommittedElkChainInfoRef.current = [];
    lastCommittedElkEdgePathsRef.current = {};
    lastCommittedLayoutPinKeyRef.current = null;
    resetAnimatedTidyToolbarSession();
    setElkLayoutResult(null);
  }, [campaignId, resetAnimatedTidyToolbarSession]);

  usePostMeasureElkRelayout({
    postMeasureElkRelayoutDoneRef,
    taskIdSignature,
    nodeDimensions,
    isMultiCampaignMode,
    tasksForLayoutRef,
    nodeDimensionsRef,
    setLayoutVersion,
    layoutVersion,
    layoutHoldRef,
    hasActiveFilters,
  });

  useElkRelayoutOnNodeDimensionsChange({
    postMeasureElkRelayoutDoneRef,
    taskIdSignature,
    nodeDimensions,
    isMultiCampaignMode,
    tasksForLayoutRef,
    nodeDimensionsRef,
    layoutRequestModeRef,
    setLayoutVersion,
    layoutVersion,
    layoutHoldRef,
    beginLayoutHold,
  });

  const chainOrderDep = chainOrder ? chainOrder.join(',') : null;
  const multiCampaignTaskIdDep = isMultiCampaignMode ? taskIdSignature : null;

  const layoutResult = useMemo(() => {
    const layoutTasks = tasksForLayoutRef.current;
    const currentNodeDimensions = nodeDimensionsRef.current;

    if (!isMultiCampaignMode) {
      return { positions: {}, chainInfo: [] };
    }

    return computeMultiCampaignLayout(layoutTasks, currentNodeDimensions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layoutVersion,
    lockedTaskIdSignature,
    lockedDependencySignature,
    dependencySignature,
    isMultiCampaignMode,
    multiCampaignTaskIdDep,
    chainOrderDep,
  ]);

  useElkAsyncLayoutEffect({
    isMultiCampaignMode,
    tasksForLayoutRef,
    nodeDimensionsRef,
    chainOrder,
    customPositionsRef,
    clearCustomPositionsBeforeElkApplyRef,
    layoutVersion,
    layoutVersionRef,
    lockedTaskIdSignature,
    lockedDependencySignature,
    scheduleLayoutEdgeRevealAfter,
    startLayoutEdgeHiddenPhase,
    setElkLayoutResult,
    initialElkSettlementRef,
    lastCommittedElkPositionsRef,
    lastCommittedElkEdgePathsRef,
    lastCommittedLayoutPinKeyRef,
    layoutRequestModeRef,
    campaignId,
    connectLayoutCenterRef,
    pendingCenterTaskIdRef,
    layoutHoldRef,
    endLayoutHold,
  });

  const legacyPositions = layoutResult.positions;
  const legacyChainInfo = layoutResult.chainInfo || [];

  const elkMatchesCurrentLayoutVersion =
    elkLayoutResult && elkLayoutResult[ELK_FOR_LAYOUT_VERSION] === layoutVersion;

  const elkPositionsOk =
    elkMatchesCurrentLayoutVersion &&
    elkLayoutResult.positions &&
    Object.keys(elkLayoutResult.positions).length > 0;

  // True once ELK has committed a layout computed AFTER all node dimensions were measured
  // (post-measure relayout or an explicit Tidy). Before that, ELK geometry is based on
  // default dims and must not be treated as the session's settled layout.
  const measuredElkSettled =
    !isMultiCampaignMode && elkPositionsOk && postMeasureElkRelayoutDoneRef.current;

  // Only a layout from real measured sizes may become the "held" graph. The
  // first pass after a remount (doc sheet closes, canvas mounts cold) still
  // uses default 290×150 cards. Remembering those coordinates makes every
  // later implicit pass — remeasure, deleted line — snap back onto the pack,
  // so full-size cards overlap until an explicit Tidy.
  if (!isMultiCampaignMode && measuredElkSettled && elkLayoutResult.positions) {
    lastCommittedElkPositionsRef.current = { ...elkLayoutResult.positions };
    lastCommittedElkChainInfoRef.current = [...(elkLayoutResult.chainInfo || [])];
    lastCommittedElkEdgePathsRef.current = { ...(elkLayoutResult.edgePaths || {}) };
    if (elkLayoutResult.__layoutPinKey) {
      lastCommittedLayoutPinKeyRef.current = elkLayoutResult.__layoutPinKey;
    }
  }

  const autoPositions = isMultiCampaignMode
    ? legacyPositions
    : elkPositionsOk
      ? elkLayoutResult.positions
      : { ...lastCommittedElkPositionsRef.current };

  const chainInfo = isMultiCampaignMode
    ? legacyChainInfo
    : elkPositionsOk
      ? elkLayoutResult.chainInfo || []
      : [...lastCommittedElkChainInfoRef.current];

  const elkEdgePaths = (() => {
    if (isMultiCampaignMode) return EMPTY_ELK_EDGE_PATHS;
    if (elkPositionsOk) return elkLayoutResult.edgePaths || EMPTY_ELK_EDGE_PATHS;
    return EMPTY_ELK_EDGE_PATHS;
  })();

  const committedElkLayoutVersion =
    elkPositionsOk &&
    elkLayoutResult &&
    elkLayoutResult[ELK_FOR_LAYOUT_VERSION] != null
      ? elkLayoutResult[ELK_FOR_LAYOUT_VERSION]
      : -1;

  const triggerLayout = useCallback(() => {
    // Suppress / cancel the pending "all nodes measured" relayout so Tidy (or any explicit
    // layout) does not get a second ELK pass ~POST_MEASURE_ELK_DELAY_MS later (edge snap).
    postMeasureElkRelayoutDoneRef.current = true;
    beginLayoutHold();
    layoutRequestModeRef.current = 'animate';
    animatedTidyToolbarArmedRef.current = true;
    setIsAnimatedTidyToolbarSession(true);
    // Hide edges immediately on Tidy — ELK + node slide run while opacity: 0 (see edges.css).
    startLayoutEdgeHiddenPhase();
    setLayoutVersion((v) => v + 1);
  }, [startLayoutEdgeHiddenPhase, beginLayoutHold]);

  const triggerLayoutInstant = useCallback(() => {
    postMeasureElkRelayoutDoneRef.current = true;
    layoutRequestModeRef.current = 'instant';
    // Cancel any "edges hidden for animated tidy" state so instant layout shows edges.
    scheduleLayoutEdgeRevealAfter(0);
    setLayoutVersion((v) => v + 1);
  }, [scheduleLayoutEdgeRevealAfter]);

  // Compact after deletes. Paste tidy is owned by the paste handlers (full autoLayout).
  useEffect(() => {
    if (isMultiCampaignMode) {
      pasteAutoLayoutPrevIdsRef.current = new Set(tasks.map((t) => t.id));
      return;
    }
    const prev = pasteAutoLayoutPrevIdsRef.current;
    const currentIds = tasks.map((t) => t.id);
    if (prev === null) {
      pasteAutoLayoutPrevIdsRef.current = new Set(currentIds);
      return;
    }
    const newIds = currentIds.filter((id) => !prev.has(id));
    const removedIds = [...prev].filter((id) => !currentIds.includes(id));
    pasteAutoLayoutPrevIdsRef.current = new Set(currentIds);
    if (!hasActiveFilters && removedIds.length > 0 && newIds.length === 0) {
      triggerLayout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdSignature, isMultiCampaignMode, triggerLayout, hasActiveFilters]);

  useEffect(() => {
    return () => {
      if (layoutAnimationTimeoutRef.current) {
        clearTimeout(layoutAnimationTimeoutRef.current);
      }
      if (edgeFadeTimeoutRef.current) {
        clearTimeout(edgeFadeTimeoutRef.current);
      }
      if (layoutHoldTimeoutRef.current != null) {
        clearTimeout(layoutHoldTimeoutRef.current);
      }
    };
  }, [layoutAnimationTimeoutRef, edgeFadeTimeoutRef]);

  return {
    autoPositions,
    chainInfo,
    elkEdgePaths,
    elkPositionsOk,
    measuredElkSettled,
    isAnimatingLayout,
    isAnimatedTidyToolbarSession,
    layoutVersion,
    committedElkLayoutVersion,
    triggerLayout,
    triggerLayoutInstant,
    taskIdSignature,
    dependencySignature,
    useAutoPositions: isAnimatingLayout,
    beginLayoutHold,
    layoutHoldRef,
  };
}

export default useFlowchartLayout;
